import React, { useState, useEffect } from 'react';
import { 
  Compass, 
  Save, 
  Clock, 
  Edit3, 
  Plus, 
  Trash2, 
  X,
  Heart,
  Leaf
} from 'lucide-react';
import { useToast } from '../../../components/Toast';
import './FormsUnified.css';

const ZONE_CONFIG = {
  easternCulture: {
    label: 'Eastern Culture Value (Roots)',
    icon: Compass,
    color: '#ea580c',
    description: 'Cultural heritage, respect, filial piety, self-discipline and spiritual foundations'
  },
  sel: {
    label: 'Social Emotional Learning (Trunk)',
    icon: Heart,
    color: '#7c3aed',
    description: 'Self-awareness, self-management, social awareness, relationship skills, decision-making'
  },
  wellbeing: {
    label: 'Wellbeing (Leaves & Canopy)',
    icon: Leaf,
    color: '#16a34a',
    description: 'Physical, social, and emotional wellness for lifelong flourishing'
  }
};

export default function StudentLifeCharacterShapingForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();

  const [formData, setFormData] = useState({
    kicker: '',
    title: '',
    description: '',
    zones: {
      easternCulture: { title: '', subtitle: '', icon: '', pillars: [] },
      sel: { title: '', subtitle: '', icon: '', pillars: [] },
      wellbeing: { title: '', subtitle: '', icon: '', pillars: [] }
    }
  });

  const [pendingFields, setPendingFields] = useState({
    kicker: false,
    title: false,
    description: false,
    zone_easternCulture: false,
    zone_sel: false,
    zone_wellbeing: false
  });

  const [originalData, setOriginalData] = useState({ ...formData });
  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [modalOpenZone, setModalOpenZone] = useState(null);
  const [modalZoneData, setModalZoneData] = useState(null);

  const getAuthHeaders = (extra = {}) => {
    const token =
      localStorage.getItem('ph_auth_token') ||
      localStorage.getItem('auth_token') ||
      sessionStorage.getItem('ph_auth_token') ||
      sessionStorage.getItem('auth_token');
    return {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...extra
    };
  };

  useEffect(() => {
    loadSection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadSection = async () => {
    setLoading(true);
    try {
      let csSection = null;

      const adminResponse = await fetch(
        `${apiBase}/studentlife/cms/sections?_t=${Date.now()}`,
        {
          credentials: 'include',
          headers: getAuthHeaders({ 'Cache-Control': 'no-cache' })
        }
      );

      if (adminResponse.ok) {
        const result = await adminResponse.json();
        if (result.ok && Array.isArray(result.data)) {
          csSection = result.data.find(s => s.section_type === 'student-life-character-shaping');
        }
      }

      if (!csSection) {
        const pubResponse = await fetch(
          `${apiBase}/studentlife/cms/sections?published=true&_t=${Date.now()}`,
          {
            credentials: 'include',
            headers: getAuthHeaders({ 'Cache-Control': 'no-cache' })
          }
        );
        if (pubResponse.ok) {
          const result = await pubResponse.json();
          if (result.ok && Array.isArray(result.data)) {
            csSection = result.data.find(s => s.section_type === 'student-life-character-shaping');
          }
        }
      }

      if (!csSection) {
        throw new Error('Character Shaping section not found');
      }

      setSectionId(csSection.id);

      const content = csSection.content_published || csSection.content || {};
      const parsedContent = typeof content === 'string' ? JSON.parse(content) : content;
      const enData = parsedContent.en || parsedContent;

      const defaultZones = {
        easternCulture: {
          title: 'Eastern Culture Value',
          subtitle: 'The Root: Respect, harmony, and shared responsibility',
          icon: 'fa-om',
          pillars: [
            { heading: 'Core Philosophy', points: ['Filial piety and respect for elders', 'Harmony and collective harmony', 'Humility and self-reflection'] },
            { heading: 'Cultural Heritage', points: ['Traditional festivals and values', 'Mindful self-cultivation', 'Community stewardship'] }
          ]
        },
        sel: {
          title: 'Social Emotional Learning',
          subtitle: 'The Trunk: Essential soft skills and emotional resilience',
          icon: 'fa-brain',
          pillars: [
            { heading: 'Self Awareness', points: ['Recognizing emotions and thoughts', 'Understanding personal strengths', 'Developing a growth mindset'] },
            { heading: 'Relationship Skills', points: ['Communicating clearly and listening actively', 'Resolving conflicts constructively', 'Building healthy friendships'] }
          ]
        },
        wellbeing: {
          title: 'Wellbeing',
          subtitle: 'The Leaves: Flourishing emotional and academic growth',
          icon: 'fa-heart',
          pillars: [
            { heading: 'Mental Wellness', points: ['Daily mindfulness and reflective moments', 'Dedicated counselor check-ins', 'Safe listening spaces'] },
            { heading: 'Flourishing Campus', points: ['Physical activity and wellness routines', 'Positive peer coaching', 'Balance of study and rest'] }
          ]
        }
      };

      const loadedZones = {};
      Object.keys(ZONE_CONFIG).forEach(key => {
        const rawZone = enData.zones?.[key] || defaultZones[key];
        let pillars = [];
        if (Array.isArray(rawZone.pillars)) {
          pillars = rawZone.pillars;
        } else if (Array.isArray(rawZone.content)) {
          pillars = rawZone.content;
        } else {
          pillars = defaultZones[key].pillars;
        }

        loadedZones[key] = {
          title: rawZone.title || defaultZones[key].title,
          subtitle: rawZone.subtitle || defaultZones[key].subtitle,
          icon: rawZone.icon || defaultZones[key].icon,
          pillars: pillars
        };
      });

      const initialData = {
        kicker: enData.kicker || 'Character Shaping Framework',
        title: enData.title || 'Character Shaping Program',
        description: enData.description || 'A holistic approach to student development built on three interconnected botanical pillars: Eastern Culture values as our roots, Social Emotional Learning as our sturdy trunk, and Wellbeing as our flourishing fruits.',
        zones: loadedZones
      };

      const pendingMap = {
        kicker: false,
        title: false,
        description: false,
        zone_easternCulture: false,
        zone_sel: false,
        zone_wellbeing: false
      };

      try {
        const pendingRes = await fetch(`${apiBase}/cms/section/${csSection.id}/with-pending`, {
          credentials: 'include',
          headers: getAuthHeaders({ 'Content-Type': 'application/json' })
        });

        if (pendingRes.ok) {
          const pendingData = await pendingRes.json();
          if (pendingData.success && pendingData.data?.pending_changes) {
            pendingData.data.pending_changes.forEach(change => {
              if (change.status === 'pending' && change.locale === 'en') {
                if (change.field_id in pendingMap) {
                  pendingMap[change.field_id] = true;
                  if (change.new_value !== null && change.new_value !== undefined) {
                    initialData[change.field_id] = change.new_value;
                  }
                } else if (change.field_id?.startsWith('zones.')) {
                  const zKey = change.field_id.split('.')[1];
                  if (zKey && ZONE_CONFIG[zKey]) {
                    pendingMap[`zone_${zKey}`] = true;
                    if (change.new_value !== null && change.new_value !== undefined) {
                      let parsedVal = change.new_value;
                      if (typeof parsedVal === 'string' && (parsedVal.startsWith('{') || parsedVal.startsWith('['))) {
                        try { parsedVal = JSON.parse(parsedVal); } catch (e) {}
                      }
                      if (typeof parsedVal === 'object' && parsedVal !== null) {
                        initialData.zones[zKey] = parsedVal;
                      }
                    }
                  }
                }
              }
            });
          }
        }
      } catch (pErr) {
        console.warn('[StudentLifeCharacterShapingForm] Pending fetch error:', pErr);
      }

      setFormData(initialData);
      setOriginalData(JSON.parse(JSON.stringify(initialData)));
      setPendingFields(pendingMap);
    } catch (err) {
      console.error('[StudentLifeCharacterShapingForm] Load error:', err);
      toast.error('Failed to load Character Shaping section');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
  };

  const handleSubmitField = async (fieldName) => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    setSubmitting(prev => ({ ...prev, [fieldName]: true }));

    try {
      const fieldLabels = {
        kicker: 'Section Kicker',
        title: 'Main Heading',
        description: 'Overview Description'
      };

      const response = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: fieldName,
          field_name: fieldLabels[fieldName] || fieldName,
          new_value: formData[fieldName],
          old_value: originalData[fieldName],
          locale: 'en'
        })
      });

      const result = await response.json();
      if (result.success || response.ok) {
        toast.success('Submitted for approval');
        setPendingFields(prev => ({ ...prev, [fieldName]: true }));
        setOriginalData(prev => ({ ...prev, [fieldName]: formData[fieldName] }));
      } else {
        if (response.status === 409) {
          toast.error('This field already has a pending approval');
        } else {
          toast.error(result.error || result.message || 'Submission failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  const handleOpenZoneModal = (zoneKey) => {
    setModalOpenZone(zoneKey);
    const existing = formData.zones[zoneKey];
    setModalZoneData(JSON.parse(JSON.stringify(existing)));
  };

  const handleCloseZoneModal = () => {
    setModalOpenZone(null);
    setModalZoneData(null);
  };

  const handleAddModalPillar = () => {
    setModalZoneData(prev => ({
      ...prev,
      pillars: [
        ...(prev.pillars || []),
        { heading: 'New Focus Area', points: ['Key action point or principle'] }
      ]
    }));
  };

  const handleRemoveModalPillar = (idx) => {
    setModalZoneData(prev => ({
      ...prev,
      pillars: prev.pillars.filter((_, i) => i !== idx)
    }));
  };

  const handlePillarHeadingChange = (idx, val) => {
    setModalZoneData(prev => {
      const updated = [...prev.pillars];
      updated[idx] = { ...updated[idx], heading: val };
      return { ...prev, pillars: updated };
    });
  };

  const handleAddPoint = (pillarIdx) => {
    setModalZoneData(prev => {
      const updated = [...prev.pillars];
      const pts = [...(updated[pillarIdx].points || []), ''];
      updated[pillarIdx] = { ...updated[pillarIdx], points: pts };
      return { ...prev, pillars: updated };
    });
  };

  const handlePointChange = (pillarIdx, pointIdx, val) => {
    setModalZoneData(prev => {
      const updated = [...prev.pillars];
      const pts = [...updated[pillarIdx].points];
      pts[pointIdx] = val;
      updated[pillarIdx] = { ...updated[pillarIdx], points: pts };
      return { ...prev, pillars: updated };
    });
  };

  const handleRemovePoint = (pillarIdx, pointIdx) => {
    setModalZoneData(prev => {
      const updated = [...prev.pillars];
      const pts = updated[pillarIdx].points.filter((_, i) => i !== pointIdx);
      updated[pillarIdx] = { ...updated[pillarIdx], points: pts };
      return { ...prev, pillars: updated };
    });
  };

  const handleSubmitZoneModal = async () => {
    if (!sectionId || !modalOpenZone) return;

    const zoneKey = modalOpenZone;
    const submitKey = `zone_${zoneKey}`;
    setSubmitting(prev => ({ ...prev, [submitKey]: true }));

    try {
      const response = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: `zones.${zoneKey}`,
          field_name: `${ZONE_CONFIG[zoneKey].label}`,
          new_value: modalZoneData,
          old_value: originalData.zones[zoneKey] || {},
          locale: 'en'
        })
      });

      const result = await response.json();
      if (result.success || response.ok) {
        toast.success(`${ZONE_CONFIG[zoneKey].label} submitted for approval`);

        setFormData(prev => ({
          ...prev,
          zones: {
            ...prev.zones,
            [zoneKey]: modalZoneData
          }
        }));

        setPendingFields(prev => ({ ...prev, [submitKey]: true }));
        handleCloseZoneModal();
      } else {
        if (response.status === 409) {
          toast.error('This zone already has a pending approval');
        } else {
          toast.error(result.error || result.message || 'Submission failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [submitKey]: false }));
    }
  };

  if (loading) {
    return (
      <div className="unified-loading-container">
        <div className="unified-loading-spinner" />
        <p className="unified-loading-text">Loading Character Shaping section...</p>
      </div>
    );
  }

  return (
    <div className="unified-form-container">
      <ToastContainer />

      {/* Header */}
      <div className="unified-header">
        <div className="unified-header-icon">
          <Compass size={22} />
        </div>
        <div>
          <h2 className="unified-title">Character Shaping Program</h2>
          <p className="unified-subtitle">
            Configure the 3-tier developmental tree: Eastern Culture (Roots), SEL (Trunk), and Wellbeing (Leaves)
          </p>
        </div>
      </div>

      {/* Field 1: Kicker */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Section Tag / Kicker</label>
          {pendingFields.kicker && (
            <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            maxLength={80}
            className="unified-input"
            value={formData.kicker}
            onChange={(e) => handleFieldChange('kicker', e.target.value)}
            disabled={submitting.kicker || pendingFields.kicker}
            placeholder="e.g. Character Shaping Framework"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('kicker')}
            disabled={submitting.kicker || pendingFields.kicker || formData.kicker === originalData.kicker}
          >
            <Save size={14} /> {submitting.kicker ? 'Saving...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Field 2: Title */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Main Heading</label>
          {pendingFields.title && (
            <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            maxLength={120}
            className="unified-input"
            value={formData.title}
            onChange={(e) => handleFieldChange('title', e.target.value)}
            disabled={submitting.title || pendingFields.title}
            placeholder="e.g. Character Shaping Program"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('title')}
            disabled={submitting.title || pendingFields.title || formData.title === originalData.title}
          >
            <Save size={14} /> {submitting.title ? 'Saving...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Field 3: Description */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Overview Description</label>
          {pendingFields.description && (
            <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
          )}
        </div>
        <div className="unified-input-action-row" style={{ alignItems: 'flex-start' }}>
          <textarea
            maxLength={600}
            className="unified-textarea"
            rows={3}
            value={formData.description}
            onChange={(e) => handleFieldChange('description', e.target.value)}
            disabled={submitting.description || pendingFields.description}
            placeholder="A holistic approach to student development built on three interconnected pillars..."
          />
          <button
            type="button"
            className="unified-submit-button"
            style={{ marginTop: '2px' }}
            onClick={() => handleSubmitField('description')}
            disabled={submitting.description || pendingFields.description || formData.description === originalData.description}
          >
            <Save size={14} /> {submitting.description ? 'Saving...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Field 4: Character Shaping Zones */}
      <div className="unified-field-card">
        <div className="unified-card-header">
          <div>
            <label className="unified-field-label">Character Shaping Zones (3 Layers)</label>
            <p className="unified-field-desc">
              Three developmental layers: Roots (Eastern Culture), Trunk (SEL), and Leaves (Wellbeing)
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '10px' }}>
          {Object.entries(ZONE_CONFIG).map(([zoneKey, config]) => {
            const zoneData = formData.zones[zoneKey] || {};
            const isPending = pendingFields[`zone_${zoneKey}`];
            const IconComp = config.icon;
            const pillarsCount = (zoneData.pillars || zoneData.content || []).length;

            return (
              <div
                key={zoneKey}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  padding: '16px 20px',
                  background: '#f8fafc',
                  borderRadius: '12px',
                  border: isPending ? '2px solid #f59e0b' : '1px solid #e2e8f0',
                  borderLeft: `5px solid ${config.color}`,
                  transition: 'all 0.2s ease'
                }}
              >
                {/* Icon Badge */}
                <div
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '10px',
                    background: config.color,
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                >
                  <IconComp size={22} />
                </div>

                {/* Details */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: '800', fontSize: '15px', color: '#0f172a' }}>
                      {zoneData.title || config.label}
                    </span>
                    <span
                      style={{
                        padding: '2px 8px',
                        borderRadius: '6px',
                        background: '#e2e8f0',
                        color: '#334155',
                        fontSize: '11px',
                        fontWeight: '700'
                      }}
                    >
                      {pillarsCount} Focus Areas
                    </span>
                    {isPending && (
                      <span className="unified-pending-pill" style={{ padding: '2px 8px', fontSize: '10px' }}>
                        <Clock size={10} /> Pending Review
                      </span>
                    )}
                  </div>
                  <p style={{ margin: 0, fontSize: '13px', color: '#475569' }}>
                    {zoneData.subtitle || config.description}
                  </p>
                </div>

                {/* Edit Button */}
                <div style={{ flexShrink: 0 }}>
                  <button
                    type="button"
                    className="unified-secondary-btn"
                    onClick={() => handleOpenZoneModal(zoneKey)}
                  >
                    <Edit3 size={13} /> Edit Zone Content
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Modal for editing a Zone */}
      {modalOpenZone && modalZoneData && (
        <div className="unified-modal-overlay" onClick={handleCloseZoneModal}>
          <div className="unified-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '680px' }}>
            <div className="unified-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    background: ZONE_CONFIG[modalOpenZone].color,
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {(() => {
                    const ModalIcon = ZONE_CONFIG[modalOpenZone].icon;
                    return <ModalIcon size={16} />;
                  })()}
                </div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#0f172a' }}>
                  Edit {ZONE_CONFIG[modalOpenZone].label}
                </h3>
              </div>
              <button
                type="button"
                onClick={handleCloseZoneModal}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}
              >
                <X size={20} />
              </button>
            </div>

            <div className="unified-modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              <div style={{ display: 'grid', gap: '16px' }}>
                <div>
                  <label className="unified-label" style={{ display: 'block', marginBottom: '6px' }}>Zone Title</label>
                  <input
                    type="text"
                    value={modalZoneData.title || ''}
                    onChange={(e) => setModalZoneData(prev => ({ ...prev, title: e.target.value }))}
                    className="unified-input"
                    placeholder="e.g. Eastern Culture Value"
                  />
                </div>

                <div>
                  <label className="unified-label" style={{ display: 'block', marginBottom: '6px' }}>Zone Subtitle / Metaphor</label>
                  <input
                    type="text"
                    value={modalZoneData.subtitle || ''}
                    onChange={(e) => setModalZoneData(prev => ({ ...prev, subtitle: e.target.value }))}
                    className="unified-input"
                    placeholder="e.g. The Root: Respect, harmony, and shared responsibility"
                  />
                </div>

                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <div>
                      <label className="unified-label" style={{ margin: 0 }}>Focus Areas & Curriculum Pillars</label>
                      <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>
                        Each focus area has a title and actionable bullet points
                      </p>
                    </div>
                    <button
                      type="button"
                      className="unified-secondary-btn"
                      onClick={handleAddModalPillar}
                      style={{ padding: '6px 12px', fontSize: '12px' }}
                    >
                      <Plus size={13} /> Add Focus Area
                    </button>
                  </div>

                  <div style={{ display: 'grid', gap: '14px' }}>
                    {(modalZoneData.pillars || []).map((pillar, pIdx) => (
                      <div
                        key={pIdx}
                        style={{
                          background: '#f8fafc',
                          padding: '16px',
                          borderRadius: '10px',
                          border: '1px solid #e2e8f0',
                          borderLeft: `4px solid ${ZONE_CONFIG[modalOpenZone].color}`
                        }}
                      >
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', alignItems: 'center' }}>
                          <span
                            style={{
                              fontSize: '11px',
                              fontWeight: '800',
                              color: '#64748b',
                              background: '#e2e8f0',
                              padding: '4px 8px',
                              borderRadius: '4px'
                            }}
                          >
                            #{pIdx + 1}
                          </span>
                          <input
                            type="text"
                            value={pillar.heading || ''}
                            onChange={(e) => handlePillarHeadingChange(pIdx, e.target.value)}
                            placeholder="Focus Area Heading (e.g. Self Awareness)"
                            className="unified-input"
                            style={{ fontWeight: '700', flex: 1 }}
                          />
                          <button
                            type="button"
                            className="unified-secondary-btn"
                            style={{ color: '#ef4444', borderColor: '#fca5a5', padding: '8px' }}
                            onClick={() => handleRemoveModalPillar(pIdx)}
                            title="Delete Focus Area"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>

                        {/* Bullet Points */}
                        <div style={{ paddingLeft: '8px', display: 'grid', gap: '6px' }}>
                          <span style={{ fontSize: '11.5px', fontWeight: '700', color: '#475569', marginBottom: '2px' }}>
                            Bullet Points (Press Enter to add next point):
                          </span>
                          {(pillar.points || []).map((point, ptIdx) => (
                            <div key={ptIdx} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                              <span style={{ color: ZONE_CONFIG[modalOpenZone].color, fontSize: '14px', fontWeight: 'bold' }}>•</span>
                              <input
                                type="text"
                                value={point || ''}
                                onChange={(e) => handlePointChange(pIdx, ptIdx, e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleAddPoint(pIdx);
                                  }
                                }}
                                placeholder="Add point description..."
                                className="unified-input"
                                style={{ padding: '6px 10px', fontSize: '13px' }}
                              />
                              <button
                                type="button"
                                onClick={() => handleRemovePoint(pIdx, ptIdx)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: '4px' }}
                                title="Remove point"
                              >
                                <X size={14} />
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => handleAddPoint(pIdx)}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              color: '#2563eb',
                              fontSize: '12px',
                              fontWeight: '600',
                              textAlign: 'left',
                              padding: '4px 0',
                              marginTop: '2px'
                            }}
                          >
                            + Add Bullet Point
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="unified-modal-footer">
              <button
                type="button"
                className="unified-secondary-btn"
                onClick={handleCloseZoneModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="unified-submit-button"
                onClick={handleSubmitZoneModal}
                disabled={submitting[`zone_${modalOpenZone}`]}
              >
                <Save size={14} />
                {submitting[`zone_${modalOpenZone}`] ? 'Submitting...' : 'Save & Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
